import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from 'context';
import { useContext } from 'react';

import {
  Button,
  Headings,
  ModalOverlay,
  Paragraph,
  TextNotificationIcon,
  PaperLessMailIcon,
} from '@sentara-health/optimahealth_web';
import { NavigateConst } from 'common';

interface PaperlessPreferencePopupProps {
  readonly smsConsent: boolean;
  readonly emailConsent: boolean;
  readonly closeModal: () => void;
}

function PaperlessPreferencePopup({
  smsConsent,
  emailConsent,
  closeModal,
}: PaperlessPreferencePopupProps) {
  // Initialize translation hook
  const { t } = useTranslation();

  // Initialize navigation hook
  const navigate = useNavigate();
  const { getFeature } = useContext(GlobalContext); // Access global feature flags
  const textNotificationsFeature = getFeature?.TextNotifications ?? false;
  const emailConsentFeature = getFeature?.EmailConsent ?? false;

  return (
    <ModalOverlay
      data-testid={'preferencesInformationId'}
      isOpen
      onClose={closeModal}
      overlayInfo="preferences information"
    >
      <Headings
        level={5}
        text={t('dashboard.preferenceTitle')}
        variant="medium"
        className="mt-1"
      />
      {(() => {
        let headerText = '';
        if (smsConsent && emailConsent) {
          headerText = t('dashboard.headerPaperlessAndText');
        } else if (smsConsent) {
          headerText = t('dashboard.headerTextNotifications');
        } else {
          headerText = t('dashboard.headerPaperlessCommunications');
        }
        return (
          <Paragraph
            text={headerText}
            variant="regular"
            className="mt-1 mb-3"
          />
        );
      })()}
      <div className="flex-lg-row flex-md-row flex-column gap-lg-3 gap-md-3 gap-4 mb-2">
        <div className="d-flex flex-lg-row flex-md-row flex-column gap-lg-3 gap-md-3 gap-4 mb-4 set_my_preferences">
          {(emailConsent && emailConsentFeature) && (
            <div className="preferences_border w-100 mt-2">
              <div className="d-flex gap-2 align-items-center m-1">
                <PaperLessMailIcon />
                <Headings
                  level={6}
                  text={t('dashboard.paperlessCommunicationsLabel')}
                  variant="medium"
                />
              </div>
              <Paragraph
                text={t('dashboard.paperlessCommunicationsContent')}
                variant="regular"
                className="mt-0 mb-3"
              />
            </div>
          )}
          {smsConsent && textNotificationsFeature && (
            <div className="preferences_border w-100 mt-2">
              <div className="d-flex gap-2 align-items-center m-1">
                <TextNotificationIcon />
                <Headings
                  level={6}
                  text={t('dashboard.textNotificationsLabel')}
                  variant="medium"
                />
              </div>
              <Paragraph
                text={t('dashboard.textNotificationsContent')}
                variant="regular"
                className="mt-0 mb-3"
              />
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-md-6 col-12">
            <Button
              data-testid={'updatePreferencesButton'}
              variant="primary_btn"
              className="btn-md-height mb-sm-0 mb-4"
              type="submit"
              onClick={(e: any) => {
                e.preventDefault();
                closeModal();
                navigate(NavigateConst.profile);
              }}
            >
              {t('dashboard.updatePreferencesLabel')}
            </Button>
          </div>
          <div className="col-md-6 col-12">
            <Button
              data-testid={'skipButton'}
              variant="secondary_btn"
              className="btn-md-height mb-sm-0"
              onClick={() => {
                closeModal();
              }}
            >
              {t('dashboard.skipNow')}
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

export default PaperlessPreferencePopup;
